import type { FC } from "react";
import { Settings01, Tool01, Receipt, Check } from "@untitledui/icons";

interface Step {
  number: string;
  title: string;
  description: string;
  icon: FC<{ className?: string }>;
  color: string;
  bullets: string[];
}

const steps: Step[] = [
  {
    number: "01",
    title: "Set Up Your Shop",
    description:
      "Add your services, set pricing, and configure storage units. Import your existing customer and vehicle records.",
    icon: Settings01,
    color: "bg-brand-100 text-brand-600",
    bullets: [
      "Define your service catalog & pricing",
      "Configure storage units & rates",
      "Add your team members",
    ],
  },
  {
    number: "02",
    title: "Manage Daily Operations",
    description:
      "Create jobs when vehicles come in, assign technicians, track parts used, and update status as work progresses.",
    icon: Tool01,
    color: "bg-success-100 text-success-600",
    bullets: [
      "Create & assign work orders",
      "Track job status in real time",
      "Manage parts and inventory levels",
    ],
  },
  {
    number: "03",
    title: "Invoice & Get Paid",
    description:
      "Generate invoices from completed jobs or storage rentals with automatic line-item breakdowns. Track what's paid and what's due.",
    icon: Receipt,
    color: "bg-warning-100 text-warning-600",
    bullets: [
      "One-click invoice generation",
      "Itemized parts + labor + tax",
      "Payment tracking & due dates",
    ],
  },
];

export default function HowItWorks() {
  return (
    <section id="how-it-works" className="section-padding">
      <div className="landing-container">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="heading-2 text-text-primary mb-4">
            Up and Running in{" "}
            <span className="text-gradient">Minutes</span>
          </h2>
          <p className="subtitle-2">
            Get your shop on the platform fast. No complex setup, no training required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className="relative bg-bg-primary rounded-xl p-6 card-shadow-hover border border-border-secondary animate-fade-in"
              style={{ animationDelay: `${index * 150}ms` }}
            >
              <div className="flex items-start justify-between mb-4">
                <span className="text-4xl font-bold text-brand-100">
                  {step.number}
                </span>
                <div
                  className={`w-10 h-10 rounded-lg flex items-center justify-center ${step.color}`}
                >
                  <step.icon className="w-5 h-5" />
                </div>
              </div>

              <h3 className="text-lg font-semibold text-text-primary mb-2">
                {step.title}
              </h3>
              <p className="text-sm text-text-tertiary mb-4 leading-relaxed">
                {step.description}
              </p>

              <ul className="space-y-2">
                {step.bullets.map((bullet) => (
                  <li key={bullet} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-success-500 mt-0.5 shrink-0" />
                    <span className="text-sm text-text-secondary">{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
